/**
  lightweight markdown renderer (currently for the AI SOPs v0 sales demo).

  Supports:
  - bold (**text**),
  - ordered lists (1. item),
  - unordered lists (- item),
  - one level of nested/indented sub-lists,
  - paragraph separation via double newlines.

  Introduced to avoid the 111KB react-markdown dep for a single, trivial use case.
  Will revisit when building the live e2e version (see epic ENG-26817).
*/
import React from 'react'

import {
  Box,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
} from '@chakra-ui/react'

interface SimpleMarkdownIProps {
  text: string
}

const formatInline = (str: string, keyPrefix: string): React.ReactNode[] =>
  str.split(/(\*\*.*?\*\*)/).map((part, i) => {
    const bold = part.match(/^\*\*(.*?)\*\*$/)
    if (bold) {
      return (
        <Text as='span' fontWeight={600} key={`${keyPrefix}-${i}`}>
          {bold[1]}
        </Text>
      )
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>
  })

const OL_REGEX = /^\d+\.\s+(.*)/
const UL_REGEX = /^[-*]\s+(.*)/
const NESTED_OL_REGEX = /^\s+\d+\.\s+(.*)/
const NESTED_UL_REGEX = /^\s+[-*]\s+(.*)/

interface PendingItemI {
  content: React.ReactNode
  key: string
  subItems: React.ReactNode[]
  subType: 'ol' | 'ul' | null
}

export const SimpleMarkdown = ({ text }: SimpleMarkdownIProps) => {
  const lines = text.split('\n')
  const elements: React.ReactNode[] = []
  let pendingItems: PendingItemI[] = []
  let listType: 'ol' | 'ul' | null = null

  const buildListItem = (item: PendingItemI): React.ReactNode => {
    if (item.subItems.length === 0) {
      return <ListItem key={item.key}>{item.content}</ListItem>
    }
    const SubList = item.subType === 'ol' ? OrderedList : UnorderedList
    return (
      <ListItem key={item.key}>
        {item.content}
        <SubList mt={1} pl={2} spacing={1}>
          {item.subItems}
        </SubList>
      </ListItem>
    )
  }

  const flushList = () => {
    if (pendingItems.length > 0 && listType) {
      const ListComponent = listType === 'ol' ? OrderedList : UnorderedList
      elements.push(
        <ListComponent
          key={`list-${elements.length}`}
          mb={2}
          pl={2}
          spacing={1}
        >
          {pendingItems.map(buildListItem)}
        </ListComponent>
      )
      pendingItems = []
      listType = null
    }
  }

  lines.forEach((line, i) => {
    const olMatch = line.match(OL_REGEX)
    const ulMatch = line.match(UL_REGEX)
    const nestedOlMatch = line.match(NESTED_OL_REGEX)
    const nestedUlMatch = line.match(NESTED_UL_REGEX)

    if (olMatch) {
      if (listType && listType !== 'ol') flushList()
      listType = 'ol'
      pendingItems.push({
        content: formatInline(olMatch[1]!, `li-${i}`),
        key: `li-${i}`,
        subItems: [],
        subType: null,
      })
    } else if (ulMatch) {
      if (listType && listType !== 'ul') flushList()
      listType = 'ul'
      pendingItems.push({
        content: formatInline(ulMatch[1]!, `li-${i}`),
        key: `li-${i}`,
        subItems: [],
        subType: null,
      })
    } else if ((nestedUlMatch || nestedOlMatch) && pendingItems.length > 0) {
      const parent = pendingItems[pendingItems.length - 1]!
      if (nestedUlMatch) {
        parent.subType = parent.subType ?? 'ul'
        parent.subItems.push(
          <ListItem key={`li-${i}`}>
            {formatInline(nestedUlMatch[1]!, `li-${i}`)}
          </ListItem>
        )
      } else {
        parent.subType = parent.subType ?? 'ol'
        parent.subItems.push(
          <ListItem key={`li-${i}`}>
            {formatInline(nestedOlMatch![1]!, `li-${i}`)}
          </ListItem>
        )
      }
    } else {
      flushList()
      const trimmed = line.trim()
      if (trimmed) {
        elements.push(
          <Text key={`p-${i}`} mb={2}>
            {formatInline(trimmed, `p-${i}`)}
          </Text>
        )
      }
    }
  })

  flushList()

  return <Box>{elements}</Box>
}
